import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  FolderKanban,
  Search,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronRight,
  ListTodo,
  DollarSign,
  User,
} from "lucide-react";
import { cn } from "../lib/utils";
import { tr } from "../components/client-portal/i18n";

const ClientProjectsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatAmount } = useCurrency();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await api.get("/client/portal");
      setProjects(res.data.projects || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error(tr("Не вдалося завантажити проєкти"));
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "active":
      case "in_progress":
        return { color: "bg-orange-500/10 text-orange-400 border-orange-500/30", icon: Clock, label: tr("В роботі") };
      case "completed":
      case "delivered":
        return { color: "bg-green-500/10 text-green-400 border-green-500/30", icon: CheckCircle2, label: tr("Завершено") };
      case "awaiting_client":
        return { color: "bg-amber-500/10 text-amber-400 border-amber-500/30", icon: AlertCircle, label: tr("Очікує на вас") };
      case "awaiting_payment":
        return { color: "bg-amber-500/10 text-amber-400 border-amber-500/30", icon: AlertCircle, label: tr("Очікує оплати") };
      case "on_hold":
        return { color: "bg-amber-500/10 text-amber-400 border-amber-500/30", icon: AlertCircle, label: tr("Призупинено") };
      case "proposal_sent":
        return { color: "bg-sky-500/10 text-sky-400 border-sky-500/30", icon: Clock, label: tr("Пропозиція надіслана") };
      case "contract_negotiation":
        return { color: "bg-sky-500/10 text-sky-400 border-sky-500/30", icon: Clock, label: tr("Узгодження договору") };
      case "sent_to_pool":
      case "new":
        return { color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30", icon: Clock, label: tr("Готується") };
      default:
        return { color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30", icon: Clock, label: (status || tr("Чернетка")).replace(/_/g, " ") };
    }
  };

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
            {tr("Кабінет клієнта")}
          </p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-50 font-['Manrope'] tracking-tight mt-1 flex items-center gap-2">
            <FolderKanban className="h-6 w-6 text-orange-500" />
            {tr("Мої проєкти")}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {tr("Переглядайте та відстежуйте свої проєкти.")}
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr("Пошук проєктів...")}
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-50 w-64 rounded-lg"
            data-testid="search-projects"
          />
        </div>
      </div>

      {/* Projects Table */}
      {filteredProjects.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800 rounded-2xl">
          <CardContent className="py-12 text-center">
            <FolderKanban className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-zinc-50 font-['Manrope']">{tr("Проєктів поки немає")}</h3>
            <p className="text-sm text-zinc-500 mt-1">
              {tr("Щойно ми почнемо роботу, ваші проєкти зʼявляться тут.")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-zinc-900/50 border-zinc-800 rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 font-medium">{tr("Проєкт")}</TableHead>
                  <TableHead className="text-zinc-400 font-medium">{tr("Статус")}</TableHead>
                  <TableHead className="text-zinc-400 font-medium">{tr("Задачі")}</TableHead>
                  <TableHead className="text-zinc-400 font-medium">{tr("Бюджет")}</TableHead>
                  <TableHead className="text-zinc-400 font-medium">{tr("Дедлайн")}</TableHead>
                  <TableHead className="text-zinc-400 font-medium">{tr("Менеджер")}</TableHead>
                  <TableHead className="text-zinc-400 font-medium w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map((project) => {
                  const statusInfo = getStatusInfo(project.status);
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <TableRow 
                      key={project.id} 
                      className="border-zinc-800 hover:bg-zinc-800/50 cursor-pointer"
                      onClick={() => navigate(`/client/projects/${project.id}`)}
                      data-testid={`project-row-${project.id}`}
                    >
                      <TableCell className="text-zinc-50 font-medium">
                        <div className="flex flex-col">
                          <span className="line-clamp-1">{project.name}</span>
                          {project.description && (
                            <span className="text-xs text-zinc-500 line-clamp-1">
                              {project.description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn("text-xs", statusInfo.color)}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-zinc-400 text-sm">
                          <ListTodo className="h-3.5 w-3.5" />
                          {project.tasks_count}
                        </div>
                      </TableCell>
                      <TableCell>
                        {project.budget > 0 ? (
                          <div className="flex items-center gap-1 text-emerald-400 font-medium">
                            {formatAmount(project.budget, "USD")}
                          </div>
                        ) : (
                          <span className="text-zinc-500 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {project.deadline ? (
                          <div className="flex items-center gap-1 text-zinc-400 text-sm">
                            <Calendar className="h-3.5 w-3.5" />
                            {new Date(project.deadline).toLocaleDateString()}
                          </div>
                        ) : (
                          <span className="text-zinc-500 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {project.project_manager ? (
                          <div className="flex items-center gap-2 text-zinc-400 text-sm">
                            <User className="h-3.5 w-3.5" />
                            <span className="line-clamp-1">{project.project_manager.name}</span>
                          </div>
                        ) : (
                          <span className="text-zinc-500 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-zinc-600" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientProjectsPage;
